import {SECTIONS, tagVideo} from "./taxonomy";

// A topic only surfaces as its own sub-playlist when it has at least this many
// videos; smaller topics roll up into the section's flat "More" list. (Per the
// product rule in CATALOG-API-PLAYLISTS-ASK.md — the data tags every video
// regardless; the threshold is a UI concern.)
export const PLAYLIST_THRESHOLD = 3;

// Group a flat video list into Section -> Playlist -> ordered videos.
//
// Returns:
//   {
//     intro:      [video],            // is_intro, pinned above everything
//     sections:   [{                  // only sections that have content
//       id, title, icon, order,
//       playlists: [{id, title, videos: [video]}],  // >= threshold, title-sorted
//       looseVideos: [video],         // topics under threshold, flattened
//     }],
//     untagged:   [video],            // no section/playlist resolved
//   }
export const buildCatalog = (videos, {threshold = PLAYLIST_THRESHOLD} = {}) => {
  const intro = [];
  const untagged = [];
  // section_id -> {playlists: Map<playlist_id, {id, title, items:[{video, order}]}>}
  const bySection = new Map();

  for (const video of videos || []) {
    const tag = tagVideo(video);

    if (tag.is_intro) {
      intro.push(video);
      continue;
    }
    if (!tag.section_id || !tag.playlist_id) {
      untagged.push(video);
      continue;
    }

    if (!bySection.has(tag.section_id)) {
      bySection.set(tag.section_id, {playlists: new Map()});
    }
    const section = bySection.get(tag.section_id);
    if (!section.playlists.has(tag.playlist_id)) {
      section.playlists.set(tag.playlist_id, {
        id: tag.playlist_id,
        title: tag.playlist_title || tag.playlist_id,
        items: [],
      });
    }
    section.playlists.get(tag.playlist_id).items.push({
      video,
      order: Number.isFinite(Number(tag.order_in_playlist))
        ? Number(tag.order_in_playlist)
        : null,
    });
  }

  const sections = SECTIONS.filter((s) => bySection.has(s.id))
    .map((s) => {
      const raw = bySection.get(s.id);
      const playlists = [];
      const looseVideos = [];

      for (const p of raw.playlists.values()) {
        // Order videos by order_in_playlist; null orders keep their incoming
        // order at the end (stable sort).
        const ordered = p.items
          .map((it, i) => ({...it, i}))
          .sort((a, b) => {
            if (a.order == null && b.order == null) return a.i - b.i;
            if (a.order == null) return 1;
            if (b.order == null) return -1;
            return a.order - b.order;
          })
          .map((it) => it.video);

        if (ordered.length >= threshold) {
          playlists.push({id: p.id, title: p.title, videos: ordered});
        } else {
          looseVideos.push(...ordered);
        }
      }

      playlists.sort((a, b) => a.title.localeCompare(b.title));
      return {...s, playlists, looseVideos};
    })
    // A section with no qualifying playlists AND no loose videos shouldn't show.
    .filter((s) => s.playlists.length > 0 || s.looseVideos.length > 0);

  return {intro, sections, untagged};
};
